import { useState, useEffect } from "react";
import { X, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  plates: "Plates of the Month",
  sips: "Sips of the Month",
  bakes: "Bakes of the Month",
};

export const MonthlySpecials = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [specials, setSpecials] = useState([]);
  const [month, setMonth] = useState("September");

  useEffect(() => {
    const fetchSpecials = async () => {
      try {
        await axios.post(`${API}/seed-specials`).catch(() => {});
        const res = await axios.get(`${API}/specials`);
        setSpecials(res.data);
        if (res.data.length > 0) setMonth(res.data[0].month || "August");
      } catch (e) {
        console.error("Failed to load specials", e);
      }
    };
    fetchSpecials();
  }, []);

  const grouped = specials.reduce((acc, item) => {
    const cat = item.category || "plates";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const categoryOrder = ["plates", "sips", "bakes"];

  if (specials.length === 0) return null;

  return (
    <>
      <section id="specials" className="py-20 md:py-28 bg-[#191646] relative overflow-hidden" data-testid="specials-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-[#C9A85A]/15 border border-[#C9A85A]/30 px-5 py-2 mb-4">
              <Sparkles className="text-[#C9A85A]" size={16} />
              <span className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase">Limited Time</span>
            </div>
            <h2 className="font-heading text-4xl md:text-5xl text-white font-medium tracking-tight mb-3">
              {month} Specials
            </h2>
            <p className="text-white/50 font-body max-w-md mx-auto">
              Chef's handpicked creations for the month.
            </p>
          </div>

          {/* Specials with images */}
          <div className="grid md:grid-cols-3 gap-5 mb-10">
            {categoryOrder.map((cat) => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              return (
                <div key={cat} className="bg-white/5 border border-white/10 overflow-hidden">
                  <h3 className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase px-5 pt-5 pb-3">
                    {CATEGORY_LABELS[cat] || cat}
                  </h3>
                  <div className="space-y-0">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-4 px-5 py-3 hover:bg-white/5 transition-colors">
                        {item.image_url && (
                          <img loading="lazy" src={item.image_url} alt={item.name} className="w-16 h-16 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline justify-between gap-2">
                            <h4 className="font-heading text-sm text-white font-medium mb-0.5 leading-tight">{item.name}</h4>
                            {item.price > 0 && <span className="text-[#C9A85A] text-sm font-heading font-medium flex-shrink-0">&#8377;{item.price}</span>}
                          </div>
                          <p className="text-white/40 text-[11px] font-body leading-relaxed line-clamp-2">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center">
            <button
              onClick={() => setShowPopup(true)}
              data-testid="view-specials-btn"
              className="inline-flex items-center gap-2 bg-[#C9A85A] text-[#191646] px-8 py-3 font-body font-semibold text-sm tracking-[0.1em] uppercase hover:bg-white transition-colors duration-300"
            >
              <Sparkles size={16} />
              View Full {month} Specials
            </button>
          </div>
        </div>
      </section>

      {/* Popup */}
      {showPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="specials-popup">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowPopup(false)} />
          <div className="relative bg-[#191646] border border-[#C9A85A]/30 max-w-2xl w-full max-h-[85vh] overflow-y-auto p-8 md:p-10">
            <button onClick={() => setShowPopup(false)} data-testid="close-specials-popup" className="absolute top-4 right-4 text-white/40 hover:text-white transition-colors">
              <X size={24} />
            </button>
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 mb-3">
                <Sparkles className="text-[#C9A85A]" size={18} />
                <span className="text-[#C9A85A] text-xs font-body font-semibold tracking-[0.2em] uppercase">Limited Time</span>
              </div>
              <h2 className="font-heading text-3xl md:text-4xl text-white font-medium tracking-tight">{month} Specials</h2>
            </div>
            {categoryOrder.map((cat) => {
              const items = grouped[cat];
              if (!items || items.length === 0) return null;
              return (
                <div key={cat} className="mb-8 last:mb-0">
                  <h3 className="text-[#C9A85A] text-sm font-body font-semibold tracking-[0.15em] uppercase mb-4 pb-2 border-b border-[#C9A85A]/20">
                    {CATEGORY_LABELS[cat] || cat}
                  </h3>
                  <div className="space-y-5">
                    {items.map((item) => (
                      <div key={item.id} className="flex gap-4">
                        {item.image_url && (
                          <img loading="lazy" src={item.image_url} alt={item.name} className="w-20 h-20 object-cover rounded flex-shrink-0" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-baseline justify-between gap-3">
                            <h4 className="font-heading text-lg text-white font-medium mb-1">{item.name}</h4>
                            {item.price > 0 && <span className="text-[#C9A85A] text-base font-heading font-medium flex-shrink-0">&#8377;{item.price}</span>}
                          </div>
                          <p className="text-white/50 text-sm font-body leading-relaxed">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            <div className="mt-8 pt-6 border-t border-white/10 text-center">
              <p className="text-white/30 text-xs font-body">Available for a limited time only at Kiplings, Inorbit Mall Hyderabad</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
